import { isArray, isPlainObject, set, toString } from 'lodash';
import { TBaseTag, TBaseTagContext } from '../types';
import { TTag } from './types';
import React, { ReactNode, useCallback, useMemo, useState } from 'react';
import { TBaseDataObject } from '../../../types';
import { UniversalTag } from '../../UniversalTag';
import { validate } from '@krugom/validator';
import UniversalChildren from '../../UniversalTag/UniversalChildren';

const childrenToString = (
  child: TBaseTag['children']
): { orig: TBaseTag['children']; string?: string } => {
  switch (typeof child) {
    case 'string':
      return { orig: child, string: child };
    case 'boolean':
    case 'number':
    case 'bigint':
    case 'symbol':
      return { orig: child, string: toString(child) };
    default:
      return { orig: child };
  }
};

export const CustomArray: React.FC<{
  tagProps: TTag;
  context: Partial<TBaseTagContext>;
}> = React.memo(({ tagProps, context }) => {
  const schema = useMemo(
    () => ({
      type: 'object',
      properties: {
        count: {
          type: 'object',
          properties: {
            min: {
              type: 'number',
              required: !tagProps.count?.max && tagProps.count?.default,
            },
            max: {
              type: 'number',
              required: !tagProps.count?.min && tagProps.count?.default,
            },
            default: {
              type: 'number',
              required: !tagProps.count?.min && tagProps.count?.max,
            },
          },
        },
        items: {
          type: 'object',
          properties: {
            groupName: {
              type: 'string',
              required: true,
            },
          },
        },
      },
    }),
    [tagProps]
  );

  const result = useMemo(() => validate(tagProps, schema), [tagProps, schema]);

  const [count, setCount] = useState<number>(tagProps.count?.default || 0);

  const handlePlus = useCallback(() => {
    if (!tagProps.count?.max || count < tagProps.count.max) setCount(count + 1);
  }, [count, tagProps.count?.max, tagProps.count?.default]);

  const handleMinus = useCallback(() => {
    if ((count < 0 && !tagProps.count?.min) || (tagProps.count?.min && count < tagProps.count.min))
      setCount(count - 1);
  }, [count, tagProps.count?.min, tagProps.count?.default]);

  const newContext = useMemo(
    () => ({
      ...context,
      array: {
        count,
        handlePlus,
        handleMinus,
      },
    }),
    [context, count, handlePlus, handleMinus]
  );

  if (!result.isValid) {
    console.error(
      'Тэг: $ArrayContext;',
      `Путь: ${context.path?.join('.') || 'Не указан'};`,
      result.errors
    );
    return <UniversalChildren context={context}>{tagProps.children}</UniversalChildren>;
  }

  const stringChild = childrenToString(tagProps.children);
  if (stringChild.string) return <>{stringChild.string}</>;

  const group = tagProps.itemsGroup || 'items';

  const children: ReactNode[] = [];

  if (isPlainObject(tagProps.children) && (tagProps.children as TBaseDataObject).group === group) {
    let key = 0;
    while (key < count) {
      const itemContext = { ...newContext };
      set(itemContext, 'array.key', key);
      children.push(
        <UniversalTag
          tagProps={tagProps.children as TBaseTag}
          context={itemContext}
          key={`array-item-${key}`}
        />
      );
      key++;
    }
  } else if (isArray(tagProps.children)) {
    let childKey = 0;
    for (const child of tagProps.children) {
      const stringChild = childrenToString(child);
      if (stringChild.string) {
        children.push(<>{stringChild.string}</>);
      } else if (isPlainObject(child)) {
        if ((child as TBaseTag).group === group) {
          let itemKey = 0;
          while (itemKey < count) {
            const itemContext = { ...newContext };
            set(itemContext, 'array.key', itemKey);
            children.push(
              <UniversalTag
                tagProps={child as TBaseTag}
                context={itemContext}
                key={`array-item-${childKey}-${itemKey}`}
              />
            );
            itemKey++;
          }
        } else {
          children.push(
            <UniversalTag
              tagProps={child as TBaseTag}
              context={newContext}
              key={`array-item-${childKey}`}
            />
          );
        }
      }
      childKey++;
    }
  }

  return <>{children}</>;
});

CustomArray.displayName = 'CustomArray';
